export enum AccountType {
  ADMIN = 'admin',
  UW_MANAGER = 'uw_manager',
  UNDERWRITER = 'underwriter',
  APPRAISER = 'appraiser',
  LOCK_DESK = 'lock_desk',
  LOAN_SETUP = 'loan_setup',
  DISCLOSURE_SPEC = 'disclosure_spec',
  CLOSER = 'closer',
  POST_CLOSER = 'post_closer',
  SERVICING = 'servicing',
  DRAW_SPECIALIST = 'draw_specialist',
  FEASIBILITY_ANALYST = 'feasibility_analyst',
  ACCOUNTING = 'accounting',
  NATIONAL_SALE = 'national_sale',
  OPERATION_SUPERVISOR = 'operation_supervisor',
  VENDOR_APPROVAL = 'vendor_approval',
  ACCOUNT_MANAGER = 'account_manager',
  ACCOUNT_EXECUTIVE = 'account_executive',
  BROKER = 'broker',
  RETAIL = 'retail',
  CORRESPONDENT = 'correspondent',
  TABLEFUNDER = 'tablefunder',
  BRANCH = 'branch',
  LOAN_OFFICER = 'loan_officer',
  LOAN_PROCESSOR = 'loan_processor',
  LOAN_OFFICER_RETAIL = 'loan_officer_retail',
  LOAN_PROCESSOR_RETAIL = 'loan_processor_retail',
}

export enum UserStatus {
  ALL = 'all',
  Active = 'active',
  Inactive = 'inactive',
}

export type UserOrderBy = 'companyName' | 'name' | 'email' | 'phone' | 'companyNmls' | 'accountType'

export const VerificationAccountType = [
  AccountType.BROKER,
  AccountType.CORRESPONDENT,
  AccountType.TABLEFUNDER,
  AccountType.LOAN_OFFICER,
  AccountType.LOAN_PROCESSOR,
  AccountType.LOAN_OFFICER_RETAIL,
  AccountType.LOAN_PROCESSOR_RETAIL,
  AccountType.BRANCH,
]

export const BrokerVerificationAccountType = [AccountType.BROKER, AccountType.CORRESPONDENT, AccountType.TABLEFUNDER]

export const BrokerAccountTypes = [
  AccountType.BROKER,
  AccountType.CORRESPONDENT,
  AccountType.TABLEFUNDER,
  AccountType.RETAIL,
  AccountType.BRANCH,
]

export const UnderwriterAEAccountTypes = [
  AccountType.BROKER,
  AccountType.TABLEFUNDER,
  AccountType.CORRESPONDENT,
  AccountType.RETAIL,
  AccountType.BRANCH,
  AccountType.LOAN_OFFICER,
  AccountType.LOAN_PROCESSOR,
]

export interface UserFilterQuery {
  query?: string
  status?: UserStatus
  accountType?: AccountType
  pullCreditScoreLevel?: string
  accountExecutive?: string
  orderBy?: UserOrderBy
  orderDir?: 1 | -1
  skip?: number
  count?: number
  showDeleted?: boolean
  showLoanSharing?: boolean
  showOthers?: boolean
  subscribeBroadcastEmail?: boolean
}

export const UserStatusFilters = {
  all: 'All',
  inactive: 'Lead',
  pending: 'Pending',
  active: 'Active',
}

export const permissionRequireTypes = [
  // AccountType.NATIONAL_SALE,
  AccountType.ACCOUNT_EXECUTIVE,
  AccountType.BROKER,
  AccountType.RETAIL,
  AccountType.CORRESPONDENT,
  AccountType.TABLEFUNDER,
  AccountType.BRANCH,
]

export const isNeedAccountTypeValidate = (accountType: AccountType) => {
  return permissionRequireTypes.indexOf(accountType) != -1
}

export interface UserActivityFilterQuery {
  startDate?: string
  endDate?: string
  email?: string
}

export enum UserActivityType {
  LOGIN = 'login',
  FORGET_PASSWORD = 'forget_password',
  RESET_PASSWORD = 'reset_password',
  REGISTER_USER = 'register_user',
  CREATE_USER = 'create_user',
  UPDATE_USER = 'update_user',
  UPDATE_LOAN_PROPERTY = 'update_loan_property',
}

export const originalAccountTypes: Record<string, string> = {
  admin: 'Admin',
  uw_manager: 'UW Manager',
  underwriter: 'Underwriter',
  appraiser: 'Appraiser',
  loan_setup: 'Loan Setup',
  disclosure_spec: 'Disclosure Specialist',
  lock_desk: 'Lock Desk',
  national_sale: 'National Sales',
  operation_supervisor: 'Operation Supervisor',
  vendor_approval: 'Vendor Approval',
  closer: 'Closer',
  post_closer: 'Post Closer',
  servicing: 'Servicing',
  draw_specialist: 'Draw Specialist',
  feasibility_analyst: 'Feasibility Analyst',
  accounting: 'Accounting',
  account_manager: 'Account Manager',
  account_executive: 'Account Executive',
  broker: 'Broker',
  retail: 'Retail',
  correspondent: 'Correspondent',
  tablefunder: 'Table Funder',
  branch: 'Branch',
  loan_officer: 'Loan Officer - Third Party',
  loan_processor: 'Loan Processor - Third Party',
  loan_officer_retail: 'Loan Officer - Retail',
  loan_processor_retail: 'Loan Processor - Retail',
}

export let accountTypes = originalAccountTypes

export const setAccountTypes = (data: Record<string, string>) => {
  accountTypes = data
}

export const accountTypeWithBorrower = (): Record<string, any> => ({ ...accountTypes, borrower: 'Borrower' })

export const accountTypeWithBorrowerAndNonLoggedUser = (): Record<string, any> => ({
  ...accountTypes,
  borrower: 'Borrower',
  non_loggedin_user: 'Non Logged In User',
})

export const getAssignedToAccountTypes = () =>
  [
    'account_executive',
    'account_manager',
    'accounting',
    'admin',
    'appraiser',
    'closer',
    'disclosure_spec',
    'draw_specialist',
    'feasibility_analyst',
    'loan_setup',
    'lock_desk',
    'national_sale',
    'operation_supervisor',
    'post_closer',
    'servicing',
    'underwriter',
    'uw_manager',
    'vendor_approval',
    'self_account_executive',
    'self_underwriter',
  ].filter((v) => Object.keys(accountTypes).includes(v))

export const accountTypeOptions = (accountType: string): Record<string, string> => {
  let rlt = accountTypes
  switch (accountType) {
    case AccountType.ACCOUNT_EXECUTIVE:
    case AccountType.NATIONAL_SALE:
      rlt = {
        broker: accountTypes[AccountType.BROKER],
        retail: accountTypes[AccountType.RETAIL],
        correspondent: accountTypes[AccountType.CORRESPONDENT],
        tablefunder: accountTypes[AccountType.TABLEFUNDER],
        branch: accountTypes[AccountType.BRANCH],
        loan_officer: accountTypes[AccountType.LOAN_OFFICER],
        loan_processor: accountTypes[AccountType.LOAN_PROCESSOR],
      }
      break
    case AccountType.BROKER:
    case AccountType.RETAIL:
    case AccountType.CORRESPONDENT:
    case AccountType.TABLEFUNDER:
      rlt = {
        branch: accountTypes[AccountType.BRANCH],
        loan_officer: accountTypes[AccountType.LOAN_OFFICER],
        loan_processor: accountTypes[AccountType.LOAN_PROCESSOR],
      }
      break
    case AccountType.BRANCH:
      rlt = {
        loan_officer: accountTypes[AccountType.LOAN_OFFICER],
        loan_processor: accountTypes[AccountType.LOAN_PROCESSOR],
      }
      break
  }
  return rlt
}
